import React, { useMemo, useState } from 'react'
import { ChartArea } from 'chart.js'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import classNames from 'classnames'
import { ChartNoAxesColumn, List } from 'lucide-react'
import BarChart from './BarChart'
import { gradients } from '../../lib/colors/gradients'
import { Info } from '@demandlocal/components'

export type ColumnConfig<T extends object> = {
  accessor: keyof T | string // Allow string literal types
  header: string
  axis: 'x' | 'y'
  info?: string
}

interface ChartTableViewProps<T extends object> {
  data: T[]
  columnConfigs: ColumnConfig<T>[]
  title: string
  isLoading?: boolean
}

const colors = [
  {
    header: '#3A5BCE',
    background: gradients.blue,
  },
  {
    header: '#409853',
    background: gradients.green,
  },
  {
    header: '#FF6259',
    background: gradients.red,
  },
  {
    header: '#800080',
    background: gradients.purple,
  },
  {
    header: '#FFA500',
    background: gradients.orange,
  },
  {
    header: '#FFFF00',
    background: gradients.yellow,
  },
  {
    header: '#00FFFF',
    background: gradients.cyan,
  },
  {
    header: '#FF00FF',
    background: gradients.magenta,
  },
]

export default function ChartTableView<T extends object>({
  data,
  columnConfigs,
  title,
  isLoading,
}: ChartTableViewProps<T>): React.ReactNode {
  const [selectedDisplayType, setSelectedDisplayType] = useState<'chart' | 'table'>(
    'table',
  )
  const columnHelper = createColumnHelper<T>()

  const columns = useMemo(
    () =>
      columnConfigs.map((config, idx) => {
        const columnColor =
          config.axis === 'x' ? '#f3f4f6' : colors[idx % colors.length].header

        return columnHelper.accessor(config.accessor as any, {
          header: () => (
            <div
              className={`p-[8px] w-full flex flex-row gap-2 items-center text-center justify-center`}
              style={{ backgroundColor: columnColor }}>
              <span
                className={classNames('text-sm', {
                  'text-black': config.axis === 'x',
                  'text-white': config.axis !== 'x',
                })}>
                {config.header}
              </span>
              {config.info && (
                <Info darkMode={config.axis !== 'x'} message={config.info} />
              )}
            </div>
          ),
          cell: (info) => (
            <div
              className={`size-full p-[8px] text-black`}
              style={{
                backgroundColor: `${columnColor}25`, // 25 = 15% opacity in hex
              }}>
              {info.getValue() as string}
            </div>
          ),
        })
      }),
    [columnConfigs, columnHelper],
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const xKey = useMemo(
    () => columnConfigs.find((config) => config.axis === 'x')?.accessor as keyof T,
    [columnConfigs],
  )

  const chartLabels = useMemo(
    () => data.map((item) => (xKey ? item[xKey] : item['date'])),
    [data, xKey],
  )

  return (
    <div
      className={classNames('flex flex-col', {
        'border-2 rounded-xl pt-4 pb-2 px-4': selectedDisplayType === 'chart',
      })}>
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-sm">{title}</h2>
        <div className="flex items-center justify-center gap-2">
          <List
            className={classNames('cursor-pointer', {
              'opacity-20': selectedDisplayType === 'chart',
            })}
            onClick={() => {
              setSelectedDisplayType('table')
            }}
          />
          <ChartNoAxesColumn
            className={classNames('cursor-pointer', {
              'opacity-20': selectedDisplayType === 'table',
            })}
            onClick={() => {
              setSelectedDisplayType('chart')
            }}
          />
        </div>
      </div>
      <div>
        {selectedDisplayType === 'chart' ? (
          <div className={classNames('h-64 mt-6', { 'opacity-50': isLoading })}>
            <BarChart
              // @ts-ignore
              datasets={columnConfigs
                .filter((d) => d.axis === 'y')
                ?.map((dataset, idx) => ({
                  label: dataset.header,
                  data: data.map((item) => item[dataset.accessor as keyof T]),
                  backgroundColor: colors[idx % colors.length].background,
                  borderRadius: 0,
                  borderWidth: 0,
                }))}
              type={'dataset'}
              labels={chartLabels}
              barPercentage={1}
              categoryPercentage={0.9}
              isLoading={false}
              isPreviousData={false}
              customOptions={{
                scales: {
                  y: {
                    type: 'logarithmic',
                    beginAtZero: true,
                    grid: { display: false },
                  },
                },
                plugins: {
                  legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                      padding: 20,
                    },
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'bottom',
                    color: 'white',
                    font: {
                      weight: 'bold',
                    },
                    formatter: (value) => value,
                  },
                },
              }}
            />
          </div>
        ) : (
          <div className="w-full mt-6 overflow-hidden" style={{ borderRadius: '8px' }}>
            <table className="w-full border-collapse" style={{ borderSpacing: 0 }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className="p-0"
                        style={{
                          ...(index === 0 && { borderTopLeftRadius: '8px' }),
                          ...(index === headerGroup.headers.length - 1 && {
                            borderTopRightRadius: '8px',
                          }),
                        }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    className={classNames({
                      'border-t-2 border-gray-200 ml-2': rowIndex !== 0,
                    })}>
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={cell.id}
                        className={classNames('p-0 text-sm font-bold text-start', {
                          '': cellIndex === 0,
                        })}
                        style={{
                          ...(rowIndex === table.getRowModel().rows.length - 1 &&
                            cellIndex === 0 && { borderBottomLeftRadius: '8px' }),
                          ...(rowIndex === table.getRowModel().rows.length - 1 &&
                            cellIndex === row.getVisibleCells().length - 1 && {
                              borderBottomRightRadius: '8px',
                            }),
                        }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {table.getFooterGroups().map((footerGroup, groupIndex) => (
                  <tr key={footerGroup.id} className="border-t border-gray-200">
                    {footerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className="p-2"
                        style={{
                          ...(groupIndex === table.getFooterGroups().length - 1 &&
                            index === 0 && { borderBottomLeftRadius: '8px' }),
                          ...(groupIndex === table.getFooterGroups().length - 1 &&
                            index === footerGroup.headers.length - 1 && {
                              borderBottomRightRadius: '8px',
                            }),
                        }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
